import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {FormControl} from '@angular/forms';
import {FileService} from '../../../../services/file.service';
import {HttpEvent, HttpEventType, HttpResponse} from '@angular/common/http';
import {SystemFile} from '../../../../models/system-file.model';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {from, of} from 'rxjs';
import {FileListItem} from './models/file-list-item.model';
import {FormStatus} from '../../../../enums/file-status.enum';

const DEFAULT_MAX_FILE_SIZE = 2 * 1024 * 1024;

@Component({
    selector: 'app-uploader',
    templateUrl: './uploader.component.html',
    styleUrls: ['./uploader.component.less'],
})
export class UploaderComponent implements OnChanges {
    control: FormControl = new FormControl();
    @Input() multiple = false;
    @Input() disableClick = false;
    @Input() placeholder = 'LABELS.CLICK_OR_DRAG_FILE';
    @Input() extensions: string[] = null;
    @Input() errors: { [key: string]: boolean } = null;
    @Output() listChange: EventEmitter<SystemFile[]> = new EventEmitter<SystemFile[]>();
    // list of previously uploaded files
    @Input() files: SystemFile | SystemFile[] = [];
    @Input() maxFileSize = DEFAULT_MAX_FILE_SIZE;
    @Input() repository = false;
    @Input() disableDownload = false;
    @Input() disallowDragAndDropMultipleFiles = false; // it may be unecessary, but I don't want to interfere with previous behavior
    formStatus = FormStatus;

    fileList: FileListItem[] = [];

    highlight = false;

    constructor(
        private fileService: FileService,
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.files) {
            if (this.files) {
                const systemFiles = Array.isArray(this.files) ? this.files : [this.files];
                this.fileList.push(
                    ...systemFiles
                        .filter(Boolean)
                        .map(systemFile => new FileListItem({systemFile, status: FormStatus.SUCCESS}))
                );
            } else {
                this.fileList = [];
            }
        }
    }

    handleSelectFiles(event) {
        const {files} = event.target;
        this.queueFiles(files);
    }

    handleDragEnter() {
        this.highlight = true;
    }

    handleDragLeave() {
        this.highlight = false;
    }

    handleDragOver(event) {
        event.preventDefault();
    }

    handleDropSuccess(event) {
        event.preventDefault();
        this.highlight = false;
        this.queueFiles(event.dataTransfer.files);
    }

    queueFiles(files = []) {
        if (!this.multiple) {
            this.fileList = [];
        }
        for (const file of files) {
            const fileListItem = new FileListItem({file});
            this.fileList.push(fileListItem);
        }
        this.uploadFiles();
    }

    uploadFiles() {
        const MAX_PARALLEL_QUERIES = 2;
        if (!this.multiple && this.disallowDragAndDropMultipleFiles) {
            this.fileList = this.fileList.slice(0, 1);
        }
        this.fileList.forEach((fileListItem: FileListItem) => this.validate(fileListItem));
        const files$ = from(this.fileList.filter(item => item.status === FormStatus.INIT));
        this.control.disable();

        files$.pipe(
            mergeMap(
                fileListItem => {
                    return this.fileService.postFile(fileListItem.file, this.repository)
                        .pipe(
                            map((event: HttpEvent<{}>) => {
                                if (event.type === HttpEventType.Sent) {
                                    fileListItem.status = FormStatus.PENDING;
                                } else if (event.type === HttpEventType.UploadProgress) {
                                    if (event.total > 0) {
                                        fileListItem.progress = Math.floor(event.loaded / event.total * 100);
                                    }
                                } else if (event instanceof HttpResponse) {
                                    fileListItem.status = FormStatus.SUCCESS;
                                    fileListItem.systemFile = event.body as SystemFile;
                                }
                                return fileListItem;
                            }), catchError((error) => {
                                fileListItem.status = FormStatus.INVALID;
                                fileListItem.errorMessage.push(typeof error === 'string'
                                    ? error
                                    : 'ERRORS.UPLOAD_ERROR');
                                return of(fileListItem);
                            }));
                }, MAX_PARALLEL_QUERIES),
        ).subscribe(
            () => {
                // single item changed
                // if (fileListItem.status === FormStatus.SUCCESS) {
                //     results[fileListItem?.response?.hash] = fileListItem;
                // }
            },
            (error) => {
                console.error('File upload error', error);
            },
            () => {
                // queue complete
                this.control.setValue(null);
                this.control.enable();
                this.handleListChange();
            }
        );
    }

    validate(fileListItem: FileListItem) {
        const validateFileExtension = () => {
            if (this.extensions && Array.isArray(this.extensions)) {
                const extension = fileListItem?.file?.name?.split('.').pop().toLocaleLowerCase();
                return !this.extensions.map(item => item?.toString().toLocaleLowerCase()).includes(extension);
            }
            return false;
        };
        const validateFileSize = () => fileListItem.file.size > this.maxFileSize;

        if (validateFileExtension()) {
            fileListItem.status = FormStatus.INVALID;
            fileListItem.errorMessage.push('ERRORS.INVALID_EXTENSION');
        }
        if (validateFileSize()) {
            fileListItem.status = FormStatus.INVALID;
            fileListItem.errorMessage.push('ERRORS.INVALID_FILE_SIZE');
        }
    }

    downloadItem(item: FileListItem) {
        if (item?.systemFile?.hash) {
            this.fileService.getTokenByHash(item?.systemFile?.hash);
        }
    }

    removeItem($event, index) {
        $event.preventDefault();
        $event.stopPropagation();
        this.fileList = !this.multiple
            ? []
            : this.fileList.splice(index, 1);
        this.handleListChange();
    }

    clearItems() {
        this.fileList = [];
        this.handleListChange();
    }

    handleListChange() {
        const emitValue = this.fileList
            .filter(item => item.status === FormStatus.SUCCESS)
            .map(item => item?.systemFile);
        this.listChange.emit(emitValue);
    }

    get errorList() {
        return this.errors ? Object.keys(this.errors) : null;
    }
}
